import { Container } from '@tryhackme/thm-ui-components';
import styled from 'styled-components';

export const StyledContainer = styled(Container)`
  margin-bottom: 19.7rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-bottom: 5rem;
  }
`;

export const StyledHeaderSection = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10rem;
  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-bottom: 6rem;
  }
`;

export const StyledTag = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.3rem;
  padding: 0.65rem 1.74rem 0.65rem 0.87rem;
  background: linear-gradient(90deg, rgba(82, 123, 211, 1) 0%, rgba(158, 179, 225, 1) 58%, rgba(167, 231, 255, 1) 100%);
  border-radius: 3.26rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: 0.6rem 1.4rem 0.6rem 0.8rem;
    gap: 1rem;
  }
`;

export const StyledTagGradient = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.22rem 0.87rem;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 3.26rem;
`;

export const StyledTagText = styled.span`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  text-transform: uppercase;
  font-size: 1.3rem;
  line-height: 1.33;
  color: ${({ theme }) => theme.colors.info.main};
`;

export const StyledTagContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.09rem;

  span {
    font-family: ${({ theme }) => theme.fonts.familySansPro};
    font-size: 1.74rem;
    line-height: 1;
    color: ${({ theme }) => theme.colors.background.dark};
    font-weight: ${({ theme }) => theme.fonts.weight.bold};

    @media (max-width: ${({ theme }) => theme.sizes.md}) {
      font-size: 1.4rem;
    }
  }

  svg {
    font-size: 1.3rem;
    color: ${({ theme }) => theme.colors.background.dark};
  }
`;

export const StyledBenefitsContainer = styled.div<{ $reversed?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 8rem;
  margin-bottom: 15rem;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    gap: 4rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    margin-bottom: 4.6rem;
  }

  ${({ $reversed }) =>
    $reversed &&
    `
    & > *:first-child {
      order: 2;
    }
    & > *:last-child {
      order: 1;
    }
  `}

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    flex-direction: column;
    gap: 4rem;

    ${({ $reversed }) =>
      $reversed &&
      `
      & > *:first-child {
        order: 1;
      }
      & > *:last-child {
        order: 2;
      }
    `}
  }
`;

export const StyledBenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const StyledBenefitTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 3.2rem;
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.text.main};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 2.8rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.sm}) {
    font-size: 2.4rem;
  }
`;

export const StyledBenefitFeatures = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4.6rem;
  min-height: 34rem;
  
  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    min-height: 30rem;
    margin: 3.2rem 0 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    min-height: 27.8rem;
  }
`;

export const StyledBenefitFeatureItem = styled.div<{ isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isActive }) => (isActive ? '1.6rem' : '0')};
  padding: 2rem;
  background: ${({ theme, isActive }) => (isActive ? theme.colors.secondary.light : 'transparent')};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border.main};
  border-radius: ${({ isActive }) => (isActive ? '0.5rem 0.5rem 0 0' : '0')};
  cursor: pointer;
  transition: all 0.3s ease-in-out;

  p, span {
    font-size: ${({ theme }) => theme.fonts.sizes.medium};
    line-height: 1.257;
    transition: all 0.3s ease-in-out;
  }

  span {
    font-family: ${({ theme }) => theme.fonts.familySansPro};
    line-height: 1.257;
    color: ${({ theme, isActive }) => (isActive ? theme.colors.text.main : theme.colors.grey[400])};
    font-weight: ${({ theme, isActive }) => (isActive ? theme.fonts.weight.bold : theme.fonts.weight.regular)};
  }

  p {
    color: ${({ theme }) => theme.colors.grey[400]};
    font-weight: ${({ theme }) => theme.fonts.weight.regular};
    opacity: ${({ isActive }) => (isActive ? '1' : '0')};
    max-height: ${({ isActive }) => (isActive ? '100px' : '0')};
    overflow: hidden;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    padding: ${({ isActive }) => (isActive ? '1.2rem 2rem 1.6rem 1.6rem' : '0 0 1.6rem 1.6rem')};

    span, p {
      font-size: ${({ theme }) => theme.fonts.sizes.default};
    }
  }
`;

export const StyledBenefitIcon = styled.span`
  font-size: 1.8rem;
`;

export const StyledBenefitStatBox = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 2.4rem;
  background: ${({ theme }) => theme.colors.info.light};
  border-radius: 0.5rem;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    gap: 1.6rem 2rem;
    padding: 2rem;
    align-items: start;
  }
`;

export const StyledBenefitStat = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.2rem;
`;

export const StyledBenefitStatDescription = styled.span`
  font-family: ${({ theme }) => theme.fonts.familySansPro};
  font-size: 1.8rem;
  line-height: 1.257;
  color: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.fonts.weight.regular};
  max-width: 27.3rem;
  font-style: italic;
  
  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 1.6rem;
    max-width: none;
    grid-column: 2;
    grid-row: 1 / 3;
    align-self: start;
  }
`;

export const StyledBenefitStatValue = styled.div`
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: 4rem;
  line-height: 1.149;
  color: ${({ theme }) => theme.colors.primary.main};
  font-weight: ${({ theme }) => theme.fonts.weight.semiBold};
  background: linear-gradient(90deg, rgba(82, 123, 211, 1) 0%, rgba(158, 179, 225, 1) 58%, rgba(167, 231, 255, 1) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    font-size: 3.2rem;
    grid-column: 1;
    grid-row: 1;
  }
`;

export const StyledBenefitImagePlaceholder = styled.img`
  width: 12.7rem;
  height: 4.5rem;
  border-radius: 0.5rem;

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    width: 100%;
    height: auto;
    max-width: 8.2rem;
    grid-column: 1;
    grid-row: 2;
  }
`;

export const StyledBenefitCard = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.secondary.light};
  border-radius: 0.5rem;
  min-height: 40rem;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.sizes.lg}) {
    min-height: 30rem;
  }

  @media (max-width: ${({ theme }) => theme.sizes.md}) {
    min-height: 25rem;
  }
`;

export const StyledBenefitCardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const StyledBenefitHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;
